const CHAT_INIT_TIMEOUT_MS = 2000;

/**
 * Opens and closes the Gorgias chat widget from a theme button, so the
 * widget's own floating launcher can be hidden.
 *
 * Wrap any button carrying `data-chat-trigger`:
 *
 *   <lyle-chat-trigger>
 *     <button type="button" data-chat-trigger aria-expanded="false">…</button>
 *   </lyle-chat-trigger>
 *
 * The Gorgias bundle is installed by the Gorgias Shopify app and exposes
 * `window.GorgiasChat`. If it is missing (blocked, or not installed) the
 * trigger does nothing.
 */
class LyleChatTrigger extends HTMLElement {
  #abortController = null;
  #button = null;
  #chat = null;

  connectedCallback() {
    this.#button = this.querySelector("[data-chat-trigger]");

    if (!this.#button) {
      return;
    }

    this.#abortController = new AbortController();
    this.#button.addEventListener("click", this.#handleClick, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    this.#abortController?.abort();

    if (this.#chat && typeof this.#chat.off === "function") {
      this.#chat.off("widget:opened", this.#handleOpened);
      this.#chat.off("widget:closed", this.#handleClosed);
    }

    this.#chat = null;
  }

  #handleClick = async () => {
    const chat = await this.#getChat();

    if (!chat) {
      return;
    }

    if (typeof chat.isOpen === "function" && chat.isOpen()) {
      chat.close();
      return;
    }

    chat.open();
  };

  #handleOpened = () => {
    this.#button?.setAttribute("aria-expanded", "true");
  };

  #handleClosed = () => {
    this.#button?.setAttribute("aria-expanded", "false");
  };

  async #getChat() {
    const chat = window.GorgiasChat;

    if (!chat || typeof chat.open !== "function") {
      return null;
    }

    if (this.#chat !== chat) {
      this.#chat = chat;

      if (typeof chat.on === "function") {
        chat.on("widget:opened", this.#handleOpened);
        chat.on("widget:closed", this.#handleClosed);
      }
    }

    if (typeof chat.init === "function") {
      try {
        await Promise.race([
          chat.init(),
          new Promise((resolve) => setTimeout(resolve, CHAT_INIT_TIMEOUT_MS)),
        ]);
      } catch {
        // The widget reported an initialisation error; still try to open it.
      }
    }

    return chat;
  }
}

if (!customElements.get("lyle-chat-trigger")) {
  customElements.define("lyle-chat-trigger", LyleChatTrigger);
}
